// Build the dense geometry caches for a schema-shaped track. Pure —
// returns a new object, doesn't mutate input.
//
// Densified geometry is what physics, surface lookup, rendering, and
// lap timing actually consume — schema waypoints are too sparse for
// collision and too jagged for rendering. Caches must be rebuilt
// whenever waypoints, walls, surface patches, or marker positions
// change.

import {
  densifyClosedLoop,
  densifyOpenPath,
  curvePointAt,
  forwardTangentAt,
  openSplineStartTangent,
  openSplineEndTangent,
  buildRibbonDense,
  buildVariableWidthEdgesClosed,
  buildVariableWidthEdgesOpen,
} from './spline.js';
import { segmentIntersect, nearestPointOnDense } from './geometry.js';
import { markerLineEndpoints, trackPerpendicularAt } from './markers.js';
import { comparePatchPriority } from './surfaces.js';
import { densifyAuthoredLine, buildPitRoute } from './racing-line.js';

// 14 subdivisions matches the visual fill of a smooth Catmull-Rom patch
// to well under a meter — close enough that no car notices the
// discretization. Centerline gets fewer (12) since it's used for nearest-
// point lookups where the existing prototype value works.
const SUBS_CENTERLINE = 12;
const SUBS_CACHE = 14;

// Default pit-box dimensions in meters. Sized for the default 4.6 m
// car: 8 m of length leaves ~3.4 m of working room around a parked
// car (realistic pit-crew choreography); 4 m of depth gives ~2 m
// behind a 1.9 m-wide car. These match real F1 / GT pit boxes (the
// "12 m" number people quote is the GARAGE behind the box, not the
// box itself). Tracks override via pl.boxLength / pl.boxDepth when
// matching a real-world count matters or a kart series scale fits.
const PIT_BOX_LENGTH = 8;
const PIT_BOX_DEPTH  = 4;

// How far the box's inner edge sits INSIDE the pit-lane edge (i.e.
// toward the lane center) instead of sitting flush against it. Small
// overlap so the box's tarmac paints OVER the lane's white edge line,
// reading as "this is part of the racing surface" rather than a
// separate rectangle parked next to it. 0.5 m covers the white edge
// stroke at common zoom levels.
const PIT_BOX_LANE_OVERLAP = 0.5;

// How far before the box's geometric center the AI's "stop here"
// event fires. The proportional brake controller has lag (it ramps
// up over the first ~5 m of braking instead of saturating instantly),
// so the car carries some speed past the planned stop point even
// after the back-prop schedules v=0. Firing the stop event 1.5 m
// short of the centroid lets that residual roll-out land the car
// CENTERED on the box's painted number rather than past it.
const PIT_BOX_STOP_BUFFER = 1.5;

// Build the dense caches. Returns
//   { centerline, walls, patches, pitLane, ribbons, timingLines }
// where each entry mirrors the prototype's `track.densified*` /
// `track.timingLines` shape so the simulator's downstream code can be
// extracted with minimal call-site churn.
export function buildDensifiedTrack(track) {
  const alpha = track.trackline?.alpha ?? track.alpha ?? 0.5;
  const wps = track.trackline?.waypoints ?? track.waypoints ?? [];

  // Centerline + variable-width edges. We expose `centerline` (the
  // dense polyline only) for back-compat with code that already used
  // it; `centerlineEdges` carries the inner/outer edge polylines and a
  // halfWidths array used by the simulator's surfaceAt for local-width
  // tarmac-strip detection.
  const trackWidth = track.trackline?.width ?? 16;
  const centerlineEdges = wps.length >= 3
    ? buildVariableWidthEdgesClosed(wps, trackWidth, alpha, SUBS_CENTERLINE)
    : { dense: [], inner: [], outer: [], halfWidths: [], subdivisions: SUBS_CENTERLINE };
  const centerline = centerlineEdges.dense;
  // White edge-band rails — same width math as centerlineEdges but with
  // a 0.3 m outward buffer for the painted edge stripe. Cached here so
  // the renderer doesn't rebuild it every frame.
  const bandEdges = wps.length >= 3
    ? buildVariableWidthEdgesClosed(wps, trackWidth, alpha, SUBS_CENTERLINE, 0.3)
    : null;

  const walls = (track.walls || [])
    .filter(w => w.waypoints && w.waypoints.length >= 2)
    .map(w => {
      const closed = !!w.closed && w.waypoints.length >= 3;
      const wAlpha = w.alpha ?? 0.5;
      const dense = closed
        ? densifyClosedLoop(w.waypoints, SUBS_CACHE, wAlpha)
        : densifyOpenPath(w.waypoints, SUBS_CACHE, wAlpha);
      return {
        thickness: w.thickness ?? 0.5,
        closed,
        dense,
        bbox: computePolylineBBox(dense),
      };
    });

  const patches = (track.surfacePatches || [])
    .filter(p => p.polygon && p.polygon.length >= 2)
    .map(p => {
      const closed = p.closed !== false;
      const pAlpha = p.alpha ?? 0.5;
      // Closed patches need 3+ vertices to bound area; treat 2-vertex
      // closed patches as degenerate.
      if (closed && p.polygon.length < 3) return null;
      const dense = closed
        ? densifyClosedLoop(p.polygon, SUBS_CACHE, pAlpha)
        : densifyOpenPath(p.polygon, SUBS_CACHE, pAlpha);
      return {
        surface: p.surface || 'grass',
        closed,
        thickness: p.thickness ?? 8.0,
        dense,
        bbox: computePolylineBBox(dense),
        // Render-only style fields. Carried through from the schema so
        // the renderer can iterate geometry.patches directly without
        // re-densifying or cross-referencing the schema. surfaceAt
        // ignores these. `endCap` applies to open-band non-curb
        // patches; unset = renderer default ('round').
        tarmacColor: p.tarmacColor,
        curbColor:   p.curbColor,
        endCap:      p.endCap,
      };
    })
    .filter(p => p !== null);
  // Sort by surface-type priority. surfaceAt iterates in reverse, so
  // higher-priority types win when patches overlap (curb > tarmac >
  // sand > gravel > grass). The sort is stable, so patches of the
  // same type keep their authored order.
  patches.sort(comparePatchPriority);

  // Pit lane spline + merge ribbons. Both contribute to surface lookup
  // (tarmac grip on the pit lane) AND to rendering. Ribbon tangents
  // match the spline endpoints + main track tangents so the visual +
  // collision geometry agree at the joins.
  let pitLane = null;
  const ribbons = [];
  const pl = track.pitLane;
  if (pl && pl.waypoints && pl.waypoints.length >= 2) {
    const plAlpha = pl.alpha ?? 0.5;
    const plWidth = pl.width ?? 4.0;
    const plEdges = buildVariableWidthEdgesOpen(pl.waypoints, plWidth, plAlpha, SUBS_CACHE);
    pitLane = {
      dense:       plEdges.dense,
      inner:       plEdges.inner,
      outer:       plEdges.outer,
      halfWidths:  plEdges.halfWidths,
      // Default half-width kept for callers that haven't been updated
      // to the per-point halfWidths array yet (e.g., merge-ribbon
      // builders below, where a single representative width is fine).
      halfWidth:   plWidth / 2,
      bbox:        computePolylineBBox(plEdges.dense),
    };

    if (wps.length >= 3) {
      const buildOne = (markerOnTrack, plEndpoint, trackToPit) => {
        // trackToPit === true  → ribbon goes track-marker → pit endpoint (entry)
        // trackToPit === false → ribbon goes pit endpoint → track-marker (exit)
        if (!markerOnTrack || !plEndpoint) return null;
        const trackPt = curvePointAt(wps, markerOnTrack.segmentIdx, markerOnTrack.t, alpha);
        const trackTan = forwardTangentAt(wps, markerOnTrack.segmentIdx, markerOnTrack.t, alpha);
        if (trackToPit) {
          const plTan = openSplineStartTangent(pl.waypoints, plAlpha);
          if (!plTan) return null;
          const dense = buildRibbonDense(trackPt, trackTan, plEndpoint, plTan);
          return dense ? { dense, halfWidth: plWidth / 2, bbox: computePolylineBBox(dense) } : null;
        } else {
          const plTan = openSplineEndTangent(pl.waypoints, plAlpha);
          if (!plTan) return null;
          const dense = buildRibbonDense(plEndpoint, plTan, trackPt, trackTan);
          return dense ? { dense, halfWidth: plWidth / 2, bbox: computePolylineBBox(dense) } : null;
        }
      };
      const entryRibbon = buildOne(pl.entry, pl.waypoints[0],                       true);
      const exitRibbon  = buildOne(pl.exit,  pl.waypoints[pl.waypoints.length - 1], false);
      if (entryRibbon) ribbons.push(entryRibbon);
      if (exitRibbon)  ribbons.push(exitRibbon);
      // Stash the ribbon dense arrays for buildPitRoute below — once
      // pushed to ribbons they're still the same objects, but pulling
      // them out here keeps the pit-route assembly readable.
      pitLane._entryRibbonDense = entryRibbon?.dense ?? null;
      pitLane._exitRibbonDense  = exitRibbon?.dense  ?? null;
    }
  }

  // Timing lines must build AFTER walls — their crossing-detection
  // endpoints raycast against the dense wall geometry to extend outward
  // from the visible track-width line. Sector lines aren't rendered;
  // they exist as invisible "gates" the car must pass in order before
  // an S/F crossing counts as a complete lap.
  const trackline = track.trackline ?? { waypoints: wps, alpha, width: track.width ?? 16 };
  const direction = track.direction ?? 'cw';
  const timingLines = {
    startFinish: buildTimingLine(track.startFinish,        trackline, direction, walls),
    // Optional separate "race start" line — when present, the AI's
    // out-lap timing begins HERE (first crossing) instead of at the
    // S/F line. The S/F line still ends each lap. Tracks without an
    // authored startLine fall through to "first S/F crossing starts
    // lap 1" (the original behavior).
    startLine:   buildTimingLine(track.startLine,          trackline, direction, walls),
    sector0:     buildTimingLine(track.sectors?.[0],       trackline, direction, walls),
    sector1:     buildTimingLine(track.sectors?.[1],       trackline, direction, walls),
    // Pit-lane S/F. Trigger zone is the visible pit-lane width (no
    // wall extension) — only cars actually on the pit lane should
    // fire this. The forward tangent comes from the open pit-lane
    // spline's authored direction (waypoint[0] is entry-side).
    pitStartFinish: track.pitLane?.startFinish
      ? buildPitTimingLine(track.pitLane.startFinish, track.pitLane)
      : null,
  };

  // Racing line for the AI. Prefers the authored line when present;
  // otherwise falls back to the centerline so the AI still drives —
  // just on a non-optimal path until someone authors a line in the
  // editor's Line tab. Densified with the same Catmull-Rom math as
  // the centerline / walls so the path math is consistent.
  let racingLine = null;
  if (track.racingLine && track.racingLine.waypoints && track.racingLine.waypoints.length >= 4) {
    racingLine = densifyAuthoredLine(track.racingLine.waypoints, track.racingLine.alpha ?? 0.5);
  } else if (wps.length >= 4) {
    racingLine = densifyAuthoredLine(wps, alpha);
  }

  // Arc on the racing line at the S/F line. The line's own seam (arc
  // wraparound from totalLength → 0) sits at racingLine.dense[0] —
  // wherever the user's authored waypoints happen to start, which is
  // generally NOT at S/F. Without an arcSF reference, any consumer
  // sorting cars by lapNumber × totalLen + rawArc gets a brief
  // standings scramble every time a car crosses the racing-line seam:
  // their lapNumber is unchanged but rawArc drops by ≈ totalLen, so
  // their key collapses. The race page already normalizes via
  // ((rawArc − arcSF + totalLen) % totalLen) when displaying T&S; the
  // sim-side caution-pace ranker needs the same offset to avoid
  // flipping leader/follower stamps on the front straight.
  if (racingLine && racingLine.dense.length >= 4 && timingLines.startFinish) {
    const np = nearestPointOnDense(timingLines.startFinish.center, racingLine.dense, true);
    if (np) {
      racingLine.arcSF = racingLine.cumulative[np.segmentIdx]
                       + np.t * racingLine.segLengths[np.segmentIdx];
    } else {
      racingLine.arcSF = 0;
    }
  } else if (racingLine) {
    racingLine.arcSF = 0;
  }

  // Pit route — open polyline the AI consumes when a car's pitState is
  // 'pitting'. Concatenates entry-ribbon → pit-lane spline → exit-
  // ribbon into a single path with the same shape as racingLine, so
  // pure-pursuit and back-prop work without controller-side branching.
  // Null when no pit lane is authored.
  //
  // pitEntryRacingLineArc — arc on the RACING LINE closest to where
  // the pit-route begins. The phase machine fires the racing→pitting
  // transition when a "requested" car's racing-line arc nears this
  // value (within a trigger window). Null when there's no pit route
  // or no racing line.
  let pitRoute = null;
  let pitEntryRacingLineArc = null;
  let pitSpeedZone = null;
  if (pitLane) {
    // Half-widths along the route — used by edgeProximity when the
    // active path is the pit route (so panic / courtesy work with the
    // local pit-lane half-width instead of the racing-surface one).
    // Ribbons are constant width by construction; pit-lane carries a
    // per-vertex halfWidths array. plWidth is the authored default.
    const plWidthLocal = pl.width ?? 4.0;
    const routeWidths = {
      entry: plWidthLocal / 2,
      pit:   pitLane.halfWidths,
      exit:  plWidthLocal / 2,
    };
    pitRoute = buildPitRoute(
      pitLane._entryRibbonDense,
      pitLane.dense,
      pitLane._exitRibbonDense,
      routeWidths,
    );
    if (pitRoute && racingLine && pitRoute.dense.length > 0) {
      const np = nearestPointOnDense(pitRoute.dense[0], racingLine.dense, true);
      if (np) {
        pitEntryRacingLineArc =
          racingLine.cumulative[np.segmentIdx] +
          np.t * racingLine.segLengths[np.segmentIdx];
      }
    }
    // Speed-zone arcs along the pit route. Markers are authored in
    // PIT-LANE SPLINE space (segmentIdx + t against pl.waypoints);
    // we resolve their world position via curvePointAt and project
    // it onto the dense pit-route polyline. The result is an arc
    // value the AI can compare against `car.derived.lineArc` when
    // pitting (which is also in pit-route space).
    //
    // Defaults — when a marker isn't authored, we anchor at the
    // start / end of the pit-lane spline (pl.waypoints[0] /
    // [N-1]). That excludes the entry / exit ribbons from the speed
    // zone, which matches F1 convention better than the previous
    // "limit during entire pit phase" behavior. Authoring the
    // markers shrinks the zone further (e.g., to align with the
    // painted "60 km/h" lines on the pit lane proper).
    if (pitRoute && pl && pl.waypoints && pl.waypoints.length >= 2) {
      const plAlpha = pl.alpha ?? 0.5;
      const wpsLast = pl.waypoints.length - 1;
      const startMarker = pl.speedZoneStart
        ?? { segmentIdx: 0,        t: 0 };
      const endMarker   = pl.speedZoneEnd
        ?? { segmentIdx: wpsLast - 1, t: 1 };
      const startPt = curvePointAt(pl.waypoints, startMarker.segmentIdx, startMarker.t, plAlpha);
      const endPt   = curvePointAt(pl.waypoints, endMarker.segmentIdx,   endMarker.t,   plAlpha);
      const startArc = arcOnPitRoute(startPt, pitRoute);
      const endArc   = arcOnPitRoute(endPt,   pitRoute);
      if (startArc != null && endArc != null) {
        // Defensive: if author placed end before start (marker
        // ordering swapped), use the bigger one as the actual end so
        // the AI's range check still works. Authoring UI prevents
        // this in practice but cheap to guard.
        pitSpeedZone = {
          startArc: Math.min(startArc, endArc),
          endArc:   Math.max(startArc, endArc),
        };
      }
    }
    // The stash on pitLane was a build-time-only courier; drop it
    // before returning so the geometry shape is clean.
    delete pitLane._entryRibbonDense;
    delete pitLane._exitRibbonDense;
  }

  // Pit boxes — auto-generated row of fixed-size boxes along the pit-
  // lane edge, on the authored side. Constraint: max count = floor(
  // zoneLength / boxLength); author lengthens the speed zone to add
  // capacity. Null when boxSide isn't authored or the speed zone is
  // shorter than a single box. Pass pitRoute so box centerArcs come
  // back in pit-route arc space (matching car.derived.lineArc when
  // pitting), not pit-lane-spline arc space.
  const pitBoxes = pl ? buildPitBoxes(pl, pitLane, pitRoute) : null;

  // Centerline arc-length cache. Mirrors racingLine's segLengths /
  // cumulative / totalLength so `currentLocationAt(pos)` can convert a
  // car's projected centerline segment into a single arc value, then
  // do an arc-vs-arc comparison against label arcs. Cheap O(N) build,
  // then O(1) per runtime query.
  const centerlineArcs = buildArcsForClosed(centerline);

  // Pre-compute label render + runtime fields. Each label's authoring
  // position (segIdx + t in trackline space) is resolved into world
  // coords (x, y) for the editor renderer + arc length for runtime
  // currentLocation lookup. Areas also cache their midpoint position
  // so the editor banner has a stable anchor.
  const labels = buildLabelGeometry(
    track.labels || [],
    wps,
    alpha,
    centerline,
    centerlineArcs,
  );

  const spatialIndex = buildSpatialIndex({ walls, patches, centerline, pitLane, ribbons });

  return {
    centerline,
    centerlineEdges,
    centerlineArcs,
    bandEdges,
    walls,
    patches,
    pitLane,
    ribbons,
    timingLines,
    racingLine,
    pitRoute,
    pitEntryRacingLineArc,
    pitSpeedZone,
    pitBoxes,
    labels,
    spatialIndex,
  };
}

// Project a world-space point onto the dense pit-route polyline and
// return its arc length along the route. Null when the point is too
// far from the route or the route is degenerate. Used to translate
// pit-lane speed-zone markers (authored in pit-lane spline space)
// into pit-route arc values the AI can compare against lineArc.
function arcOnPitRoute(pt, route) {
  if (!route || !route.dense || route.dense.length < 2 || !pt) return null;
  const np = nearestPointOnDense(pt, route.dense, false);
  if (!np) return null;
  return route.cumulative[np.segmentIdx] + np.t * route.segLengths[np.segmentIdx];
}

// Auto-generate pit-box quads for a pitLane with an authored boxSide.
// Exported so the editor (which doesn't cache full geometry) can call
// it directly with a freshly densified pit-lane shape and reuse the
// same quad math the runtime sees.
//
// `pitRoute` is optional. When provided, each box's `centerArc` is in
// pit-route arc space (the same space as `car.derived.lineArc` while
// pitting) — required for the runtime phase machine and AI to work.
// When omitted (editor render), centerArc falls back to pit-lane-
// spline arc space; the editor doesn't compare arcs against runtime
// car state, so the simpler value is fine there.
//
// Approach:
//   1. Build arc tables along the dense pit-lane polyline.
//   2. Resolve the speed-zone start / end markers (or fall back to the
//      full spline) onto the dense polyline → zoneStart / zoneEnd arcs.
//   3. Max count = floor(zoneLength / boxLength). No fractional boxes —
//      author lengthens the zone to add capacity (constraint locked
//      with user: cap grid, don't shrink boxes).
//   4. For each box, walk to its center arc, sample point + tangent on
//      the dense polyline, and emit a quad offset perpendicular to the
//      lane on the chosen side. Inner edge sits at the local pit-lane
//      half-width (so boxes are flush with the lane edge regardless of
//      per-waypoint width overrides); outer edge at half-width + depth.
//
// Side convention: boxSide === 'right' → quad is on the +perp side
// (perp = rotate tangent 90° CW in screen-space, i.e. (-ty, tx)).
// 'left' → -perp side. Pit-lane direction is wpts[0] → wpts[N-1].
//
// Returns null when boxSide isn't authored or the zone can't fit a
// single box. Boxes are render data + future-collision data; nothing
// in physics looks at them yet.
export function buildPitBoxes(pl, pitLane, pitRoute = null) {
  if (!pl?.boxSide || !pitLane || !pitLane.dense || pitLane.dense.length < 2) return null;
  const dense = pitLane.dense;
  const N = dense.length;
  if (N < 2) return null;

  const boxLength = pl.boxLength ?? PIT_BOX_LENGTH;
  const boxDepth  = pl.boxDepth  ?? PIT_BOX_DEPTH;
  const sideSign  = pl.boxSide === 'left' ? -1 : 1;

  // Per-segment arc cache + per-vertex unit tangent for the dense pit-
  // lane polyline. Tangents are used for the perpendicular at sampled
  // arcs (back / front of each box) AND at every interior dense vertex
  // that falls inside a box's arc range — so each box's edges trace
  // the lane curve instead of cutting a straight chord across it.
  const segLen   = new Array(N - 1);
  const cum      = new Array(N);
  const tangents = new Array(N);
  cum[0] = 0;
  let acc = 0;
  for (let i = 0; i < N - 1; i++) {
    segLen[i] = Math.hypot(dense[i + 1].x - dense[i].x, dense[i + 1].y - dense[i].y);
    acc += segLen[i];
    cum[i + 1] = acc;
  }
  const totalArc = acc;
  if (totalArc < boxLength) return null;
  for (let i = 0; i < N; i++) {
    let tx, ty;
    if (i === 0) {
      tx = dense[1].x - dense[0].x;
      ty = dense[1].y - dense[0].y;
    } else if (i === N - 1) {
      tx = dense[i].x - dense[i - 1].x;
      ty = dense[i].y - dense[i - 1].y;
    } else {
      const dx1 = dense[i].x - dense[i - 1].x;
      const dy1 = dense[i].y - dense[i - 1].y;
      const l1  = Math.hypot(dx1, dy1) || 1;
      const dx2 = dense[i + 1].x - dense[i].x;
      const dy2 = dense[i + 1].y - dense[i].y;
      const l2  = Math.hypot(dx2, dy2) || 1;
      tx = dx1 / l1 + dx2 / l2;
      ty = dy1 / l1 + dy2 / l2;
    }
    const len = Math.hypot(tx, ty) || 1;
    tangents[i] = { x: tx / len, y: ty / len };
  }

  // Resolve speed-zone markers to pit-lane spline arcs. When markers
  // aren't authored we use the full spline (matches the speed-zone
  // default in the route block above).
  const plAlpha = pl.alpha ?? 0.5;
  const wps = pl.waypoints;
  const wpsLast = wps.length - 1;
  const startMarker = pl.speedZoneStart ?? { segmentIdx: 0,            t: 0 };
  const endMarker   = pl.speedZoneEnd   ?? { segmentIdx: wpsLast - 1,  t: 1 };
  const startPt = curvePointAt(wps, startMarker.segmentIdx, startMarker.t, plAlpha);
  const endPt   = curvePointAt(wps, endMarker.segmentIdx,   endMarker.t,   plAlpha);
  const startNp = nearestPointOnDense(startPt, dense, false);
  const endNp   = nearestPointOnDense(endPt,   dense, false);
  if (!startNp || !endNp) return null;
  const startArcRaw = cum[startNp.segmentIdx] + startNp.t * (segLen[startNp.segmentIdx] ?? 0);
  const endArcRaw   = cum[endNp.segmentIdx]   + endNp.t   * (segLen[endNp.segmentIdx]   ?? 0);
  const zoneStart = Math.min(startArcRaw, endArcRaw);
  const zoneEnd   = Math.max(startArcRaw, endArcRaw);
  const zoneLen   = zoneEnd - zoneStart;
  // Keep at least one box-length of clear space between each speed-
  // zone gate and the first/last box. This buffers the run-up where
  // a car has just slowed to pit pace (and the run-out where it's
  // still at pit pace before the limiter releases) so cars never
  // collide with a parked-box neighbour right at the gate.
  const usableLen = zoneLen - 2 * boxLength;
  if (usableLen < boxLength) return null;

  const maxCount = Math.floor(usableLen / boxLength);
  if (maxCount <= 0) return null;
  const midArc = (zoneStart + zoneEnd) / 2;
  const totalSpan = maxCount * boxLength;
  const firstBack = midArc - totalSpan / 2;

  const halfWidths = pitLane.halfWidths;
  const halfWFallback = pitLane.halfWidth ?? 2;

  // Entry-ribbon offset — the arc length of the route BEFORE the pit-
  // lane spline begins. Box arcs are computed against the spline
  // (which is what `dense` indexes); converting to route arcs is
  // just adding this constant. We get it by projecting the spline's
  // first vertex onto the pit-route's dense polyline. Zero when no
  // pitRoute is provided (editor render path); centerArc then stays
  // in spline arc space — fine because the editor doesn't compare
  // box arcs against any car's runtime arc.
  let entryRibbonArc = 0;
  if (pitRoute && pitRoute.dense && pitRoute.dense.length >= 2) {
    const np = nearestPointOnDense(dense[0], pitRoute.dense, false);
    if (np) {
      entryRibbonArc =
        pitRoute.cumulative[np.segmentIdx] +
        np.t * pitRoute.segLengths[np.segmentIdx];
    }
  }

  const boxes = [];
  for (let i = 0; i < maxCount; i++) {
    const backArc  = firstBack + i * boxLength;
    const frontArc = backArc + boxLength;
    const box = pitBoxBetween(dense, segLen, cum, tangents, backArc, frontArc, boxDepth, halfWidths, halfWFallback, sideSign);
    if (box) {
      // Translate the box's spline-arc fields into route-arc space so
      // runtime comparisons against car.derived.lineArc (which is in
      // pit-route space when pitting) are unit-correct.
      box.centerArc += entryRibbonArc;
      box.backArc   += entryRibbonArc;
      box.frontArc  += entryRibbonArc;
      // stopArc — where the AI's brake-to-zero event AND the phase
      // machine's pit→boxStopped trigger both fire. Offset earlier
      // than centerArc so brake-controller lag still lands the car
      // centered on the painted number.
      box.stopArc = box.centerArc - PIT_BOX_STOP_BUFFER;
      boxes.push(box);
    }
  }
  if (boxes.length === 0) return null;
  // Lateral offset (signed, in the AI's perpendicular convention) from
  // pit-route center to the box's stopping point. Pure pursuit uses
  // this to peel a car laterally as it approaches its assigned box,
  // then peel it back to lane center on the way out. Sign matches the
  // ai-controller's perpendicular convention: +y_perp when sideSign>0
  // (box on right of tangent), -y_perp when sideSign<0.
  const lateralOffset = sideSign * (halfWFallback - PIT_BOX_LANE_OVERLAP + boxDepth / 2);
  return {
    side:      pl.boxSide,
    boxLength,
    boxDepth,
    midArc,
    zoneStart,
    zoneEnd,
    maxCount,
    boxes,
    lateralOffset,
  };
}

// Build one pit-box polygon spanning [backArc, frontArc] along the
// dense pit-lane polyline. The box's inner edge follows the lane edge
// (pit-lane half-width offset on the chosen side); the outer edge is
// a parallel offset further out by `boxDepth`. Both edges include
// every interior dense vertex that falls inside the arc range, so the
// box traces the lane curvature — straight lanes degenerate to a 4-
// corner trapezoid, curved lanes get the right number of intermediate
// vertices for the polyline to read smooth. Adjacent boxes' shared
// arc samples produce identical points (same arc → same point + same
// per-arc tangent), so the row of boxes has no gaps or overlaps along
// the lane direction regardless of how the lane bends.
function pitBoxBetween(dense, segLen, cum, tangents, backArc, frontArc, boxDepth, halfWidthsArr, halfWFallback, sideSign) {
  const sampleAtArc = (arc) => {
    let segIdx = 0;
    for (let i = 0; i < segLen.length; i++) {
      if (cum[i + 1] >= arc) { segIdx = i; break; }
      segIdx = i;
    }
    const segL = segLen[segIdx] || 1e-9;
    const t = Math.max(0, Math.min(1, (arc - cum[segIdx]) / segL));
    const p0 = dense[segIdx];
    const p1 = dense[segIdx + 1];
    const x = p0.x + (p1.x - p0.x) * t;
    const y = p0.y + (p1.y - p0.y) * t;
    const tx = (p1.x - p0.x) / segL;
    const ty = (p1.y - p0.y) / segL;
    const halfW = halfWidthsArr?.[segIdx] ?? halfWFallback;
    return { x, y, tx, ty, halfW };
  };

  const back  = sampleAtArc(backArc);
  const front = sampleAtArc(frontArc);

  // Build chain back → interior dense vertices (strictly inside the
  // arc range) → front. The strict bounds prevent duplicating a
  // vertex that lands exactly on a box boundary.
  const chain = [back];
  for (let i = 0; i < dense.length; i++) {
    if (cum[i] > backArc + 1e-6 && cum[i] < frontArc - 1e-6) {
      chain.push({
        x: dense[i].x,
        y: dense[i].y,
        tx: tangents[i].x,
        ty: tangents[i].y,
        halfW: halfWidthsArr?.[i] ?? halfWFallback,
      });
    }
  }
  chain.push(front);

  const innerEdge = new Array(chain.length);
  const outerEdge = new Array(chain.length);
  for (let i = 0; i < chain.length; i++) {
    const s = chain[i];
    const px = -s.ty * sideSign;
    const py =  s.tx * sideSign;
    // Inner edge shifts toward the lane center by PIT_BOX_LANE_OVERLAP
    // so the box tarmac paints over the lane's white edge line. Outer
    // edge shifts by the same amount so the box keeps its boxDepth.
    const innerOff = s.halfW - PIT_BOX_LANE_OVERLAP;
    const outerOff = innerOff + boxDepth;
    innerEdge[i] = { x: s.x + px * innerOff, y: s.y + py * innerOff };
    outerEdge[i] = { x: s.x + px * outerOff, y: s.y + py * outerOff };
  }

  return {
    centerArc: (backArc + frontArc) / 2,
    backArc,
    frontArc,
    innerEdge,
    outerEdge,
  };
}

// Per-vertex arc cache for a closed dense polyline:
//   segLengths[i] = ||dense[i+1 mod N] - dense[i]||
//   cumulative[i] = arc length at dense[i] from dense[0]
//   totalLength    = full closed-loop circumference
// Mirrors the racing-line shape so consumers can use one shape for both.
function buildArcsForClosed(dense) {
  const N = dense.length;
  if (N < 2) return { segLengths: [], cumulative: [], totalLength: 0 };
  const segLengths = new Array(N);
  const cumulative = new Array(N);
  let acc = 0;
  for (let i = 0; i < N; i++) {
    cumulative[i] = acc;
    const next = (i + 1) % N;
    segLengths[i] = Math.hypot(dense[next].x - dense[i].x, dense[next].y - dense[i].y);
    acc += segLengths[i];
  }
  return { segLengths, cumulative, totalLength: acc };
}

// Resolve a single (segIdx, t) in trackline-authoring space into a
// (worldX, worldY, arc) triple. Arc comes from the dense centerline:
// each authored segment maps to SUBS_CENTERLINE consecutive dense
// vertices, so dense vertex `segIdx * SUBS + t * SUBS` interpolates
// the right arc cheaply without a re-projection pass.
function resolveTracklinePos(wps, alpha, centerline, centerlineArcs, segmentIdx, t) {
  const N = wps.length;
  if (N < 3) return null;
  const segIdx = ((segmentIdx % N) + N) % N;
  const tt = Math.max(0, Math.min(1, t ?? 0));
  const point = curvePointAt(wps, segIdx, tt, alpha);
  // Map authored (segIdx, t) → dense-vertex index along the closed
  // densified centerline. Float index, then linear arc interp.
  const denseFloat = (segIdx + tt) * SUBS_CENTERLINE;
  const i0 = Math.floor(denseFloat) % centerline.length;
  const i1 = (i0 + 1) % centerline.length;
  const frac = denseFloat - Math.floor(denseFloat);
  const arc = centerlineArcs.cumulative[i0]
    + frac * centerlineArcs.segLengths[i0];
  // Wrap to [0, totalLength) just in case.
  const total = centerlineArcs.totalLength || 1;
  let arcWrapped = arc % total;
  if (arcWrapped < 0) arcWrapped += total;
  return { x: point.x, y: point.y, arc: arcWrapped, segIdx, t: tt, denseIdx: i0, denseI1: i1 };
}

function buildLabelGeometry(rawLabels, wps, alpha, centerline, centerlineArcs) {
  if (!Array.isArray(rawLabels) || rawLabels.length === 0 || wps.length < 3) {
    return [];
  }
  const out = [];
  for (let i = 0; i < rawLabels.length; i++) {
    const L = rawLabels[i];
    if (!L || typeof L.text !== 'string') continue;

    if (L.kind === 'corner') {
      const p = resolveTracklinePos(wps, alpha, centerline, centerlineArcs, L.segmentIdx ?? 0, L.t ?? 0);
      if (!p) continue;
      const perp = trackPerpendicularAt(wps, p.segIdx, p.t, alpha);
      out.push({
        kind: 'corner',
        text: L.text,
        segmentIdx: p.segIdx,
        t: p.t,
        x: p.x,
        y: p.y,
        perpX: perp.x,
        perpY: perp.y,
        arc: p.arc,
        srcIdx: i,
      });
    } else if (L.kind === 'area') {
      const ps = resolveTracklinePos(wps, alpha, centerline, centerlineArcs, L.startSegIdx ?? 0, L.startT ?? 0);
      const pe = resolveTracklinePos(wps, alpha, centerline, centerlineArcs, L.endSegIdx ?? 0, L.endT ?? 0);
      if (!ps || !pe) continue;
      const total = centerlineArcs.totalLength || 1;
      // Forward arc span from start → end (handles wraparound). The
      // midpoint sits half-way along that forward span so the banner
      // anchors visibly inside the area even when it wraps S/F.
      let span = pe.arc - ps.arc;
      if (span < 0) span += total;
      const midArcRaw = ps.arc + span / 2;
      const midArc = midArcRaw % total;
      // Map midArc back to an authored (segIdx, t) so we can get a
      // perpendicular from the trackline math (cheap + matches the
      // visual the editor actually draws).
      const perWp = total / wps.length;
      const midParam = midArc / perWp;
      const midSegIdx = Math.floor(midParam) % wps.length;
      const midT = midParam - Math.floor(midParam);
      const midPoint = curvePointAt(wps, midSegIdx, midT, alpha);
      const midPerp  = trackPerpendicularAt(wps, midSegIdx, midT, alpha);
      out.push({
        kind: 'area',
        text: L.text,
        startSegIdx: ps.segIdx,
        startT: ps.t,
        endSegIdx: pe.segIdx,
        endT: pe.t,
        startX: ps.x,
        startY: ps.y,
        endX: pe.x,
        endY: pe.y,
        startArc: ps.arc,
        endArc: pe.arc,
        midArc,
        midX: midPoint.x,
        midY: midPoint.y,
        midPerpX: midPerp.x,
        midPerpY: midPerp.y,
        srcIdx: i,
      });
    }
  }
  return out;
}

// ---------- spatial index ----------
//
// Uniform grid bucketing every polyline segment into world-space cells.
// surfaceAt + applyWallCollision walk only the cell containing the
// query point (and immediate neighbors for distance queries) instead of
// scanning every wall, every patch, the full centerline etc. on every
// car every tick. With 40 cars on mid-ohio this turns ~1500 segment
// checks per query into ~5-30.
//
// One grid is shared by all polyline kinds (walls, centerline, pit
// lane, ribbons); each entry carries a `kind` tag so per-kind queries
// filter out other entries cheaply. Patches use a separate per-cell
// list of patch indices (containment via point-in-polygon, not
// distance) and queries use only the single cell containing the point.

const SPATIAL_CELL_SIZE = 16;       // m — comfortably larger than wall/curb thickness, smaller than corner radii

// Encode (gx, gy) as a single number for Map performance. Range
// [-50000, +50000] covers tracks up to 800 km across — plenty.
const SPATIAL_KEY_OFFSET = 50000;
const SPATIAL_KEY_STRIDE = 2 * SPATIAL_KEY_OFFSET + 1;
function spatialCellKey(gx, gy) {
  return (gx + SPATIAL_KEY_OFFSET) * SPATIAL_KEY_STRIDE + (gy + SPATIAL_KEY_OFFSET);
}

function computePolylineBBox(dense) {
  if (!dense || dense.length === 0) return null;
  let minX =  Infinity, minY =  Infinity;
  let maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < dense.length; i++) {
    const p = dense[i];
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

function buildSpatialIndex({ walls, patches, centerline, pitLane, ribbons }) {
  const segCells   = new Map();   // cellKey → segment-entry[]
  const patchCells = new Map();   // cellKey → patchIdx[]

  function addSegmentToCells(ax, ay, bx, by, entry) {
    const minX = ax < bx ? ax : bx;
    const maxX = ax > bx ? ax : bx;
    const minY = ay < by ? ay : by;
    const maxY = ay > by ? ay : by;
    const gx0 = Math.floor(minX / SPATIAL_CELL_SIZE);
    const gx1 = Math.floor(maxX / SPATIAL_CELL_SIZE);
    const gy0 = Math.floor(minY / SPATIAL_CELL_SIZE);
    const gy1 = Math.floor(maxY / SPATIAL_CELL_SIZE);
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const key = spatialCellKey(gx, gy);
        let arr = segCells.get(key);
        if (!arr) { arr = []; segCells.set(key, arr); }
        arr.push(entry);
      }
    }
  }

  function indexPolyline(dense, closed, kind, ownerIdx) {
    const n = dense.length;
    if (n < 2) return;
    const segCount = closed ? n : n - 1;
    for (let i = 0; i < segCount; i++) {
      const a = dense[i];
      const b = dense[(i + 1) % n];
      addSegmentToCells(a.x, a.y, b.x, b.y, { kind, ownerIdx, segIdx: i });
    }
  }

  for (let wi = 0; wi < walls.length; wi++) {
    indexPolyline(walls[wi].dense, walls[wi].closed, 'wall', wi);
  }
  if (centerline && centerline.length >= 2) {
    indexPolyline(centerline, true, 'centerline', 0);
  }
  if (pitLane && pitLane.dense && pitLane.dense.length >= 2) {
    indexPolyline(pitLane.dense, false, 'pitLane', 0);
  }
  for (let ri = 0; ri < ribbons.length; ri++) {
    indexPolyline(ribbons[ri].dense, false, 'ribbon', ri);
  }

  // Patches: bucket by patch bbox. surfaceAt only checks the single
  // cell containing the query point; that's sufficient because a
  // patch is in every cell its bbox touches, so any patch whose bbox
  // contains the point is in the point's cell.
  for (let pi = 0; pi < patches.length; pi++) {
    const bb = patches[pi].bbox;
    if (!bb) continue;
    const gx0 = Math.floor(bb.minX / SPATIAL_CELL_SIZE);
    const gx1 = Math.floor(bb.maxX / SPATIAL_CELL_SIZE);
    const gy0 = Math.floor(bb.minY / SPATIAL_CELL_SIZE);
    const gy1 = Math.floor(bb.maxY / SPATIAL_CELL_SIZE);
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const key = spatialCellKey(gx, gy);
        let arr = patchCells.get(key);
        if (!arr) { arr = []; patchCells.set(key, arr); }
        arr.push(pi);
      }
    }
  }

  return { cellSize: SPATIAL_CELL_SIZE, segCells, patchCells, key: spatialCellKey };
}

// Build one timing line: the visible portion is the track-width line on
// the racing surface; the trigger zone extends outward to the nearest
// wall on each side so a car cutting across the grass off-track still
// trips the timer.
function buildTimingLine(marker, trackline, direction, densifiedWalls) {
  if (!marker) return null;
  const ends = markerLineEndpoints(marker, trackline, direction);
  if (!ends) return null;
  const MAX_DIST = 200;   // m — much larger than any practical track width
  const aExt = raycastToWalls(ends.center, -ends.perp.x, -ends.perp.y, MAX_DIST, densifiedWalls);
  const bExt = raycastToWalls(ends.center,  ends.perp.x,  ends.perp.y, MAX_DIST, densifiedWalls);
  return {
    center:    ends.center,
    tangent:   ends.tangent,
    visibleA:  ends.a,
    visibleB:  ends.b,
    a:         aExt,
    b:         bExt,
  };
}

// Pit-lane variant of buildTimingLine. The visible portion = the
// trigger portion (no wall extension) — cars off the pit lane
// shouldn't trip this line, even if they happen to cross its
// extrapolation. Pit lane is an open spline, so markerLineEndpoints
// runs with closed=false.
function buildPitTimingLine(marker, pitLane) {
  if (!marker) return null;
  const ends = markerLineEndpoints(marker, pitLane, 'cw', false);
  if (!ends) return null;
  return {
    center:    ends.center,
    tangent:   ends.tangent,
    visibleA:  ends.a,
    visibleB:  ends.b,
    a:         ends.a,
    b:         ends.b,
  };
}

// Cast a ray from origin in direction (dx, dy) up to maxLength; returns
// where it first hits a wall (or the ray endpoint if no wall is hit).
function raycastToWalls(origin, dx, dy, maxLength, densifiedWalls) {
  const rayEnd = { x: origin.x + dx * maxLength, y: origin.y + dy * maxLength };
  let bestT = 1, bestPoint = rayEnd;
  for (const wall of densifiedWalls) {
    const dense = wall.dense;
    const n = dense.length;
    const segCount = wall.closed ? n : n - 1;
    for (let i = 0; i < segCount; i++) {
      const a = dense[i];
      const b = dense[(i + 1) % n];
      const hit = segmentIntersect(origin, rayEnd, a, b);
      if (hit && hit.t < bestT) {
        bestT = hit.t;
        bestPoint = { x: hit.x, y: hit.y };
      }
    }
  }
  return bestPoint;
}
